import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type {
  CareEntryKind, CareEntryResponse, CatalogItemResponse, CatalogType, DailyReportResponse, MealAmountEaten,
} from '../api/types';
import { useApi } from '../auth/ApiProvider';
import { Permissions } from '../auth/permissions';
import { useSession } from '../auth/SessionProvider';
import { atTimeOfDay, timeOfDay, todayIso } from '../format';
import { useMemberSwitch } from '../hooks/useTenantSettings';
import { useI18n } from '../i18n/I18nProvider';
import { Input, Select, Textarea } from './Field';
import { Badge, Button, Card, Notice, Row, Stack } from './primitives';
import { ErrorState, LoadingState } from './states';

const AMOUNTS: MealAmountEaten[] = ['All', 'Most', 'Some', 'Little', 'Refused'];

/** The catalogue each kind chooses from, where it chooses from one. */
const CHOICE: Partial<Record<CareEntryKind, CatalogType>> = {
  Drink: 'IntakeLevel', Activity: 'Activity', Mood: 'Mood', Diaper: 'DiaperStatus',
};

const ICON: Record<CareEntryKind, string> = {
  Meal: '🍽️', Drink: '💧', Sleep: '😴', Activity: '🎨', Mood: '😊', Diaper: '🧷', Note: '📝',
};

/** Every query a change to a child's day leaves stale: the day itself, and the class board it is counted on. */
export function invalidateCareDay(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['child-day'] }),
    queryClient.invalidateQueries({ queryKey: ['class-day'] }),
  ]).then(() => undefined);
}

/**
 * One child's day as staff keep it: what was recorded, correcting and removing an entry, ending a nap, and
 * publishing the day where the nursery reviews before families read (FR-STF-10, FR-DAY-05, FR-DAY-06).
 *
 * Shared by the staff app and the admin console so the two keep one set of rules. Who may change what is the
 * server's: staff correct their own entries on the day, a holder of `daily.publish` anyone's at any time (the route's
 * own rule), and a closed term refuses both with its 409 (Q34). The buttons follow the same rule so nobody is offered
 * a change the server would refuse; when it refuses anyway, its own words are shown.
 */
export function CareDay({ childId, date }: { childId: number; date?: string }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t, formatDate, formatTime, settings } = useI18n();
  const { session, can } = useSession();
  const reviewRequired = useMemberSwitch('daily.review_required');
  const [editing, setEditing] = useState<string | null>(null);

  const day = date ?? todayIso(settings);
  const isToday = day === todayIso(settings);
  const mayPublish = can(Permissions.daily.publish);
  const me = session?.membership.membershipId;

  const report = useQuery({
    queryKey: ['child-day', childId, day],
    queryFn: () => api.daily.forChild(childId, day),
  });

  const publish = useMutation({
    mutationFn: () => api.daily.publish(childId, day),
    onSuccess: () => invalidateCareDay(queryClient),
  });

  const wake = useMutation({
    mutationFn: (entry: CareEntryResponse) => api.daily.update('Sleep', entry.id, { endAt: new Date().toISOString() }),
    onSuccess: () => invalidateCareDay(queryClient),
  });

  const remove = useMutation({
    mutationFn: (entry: CareEntryResponse) => api.daily.remove(entry.kind, entry.id),
    onSuccess: () => invalidateCareDay(queryClient),
  });

  if (report.isPending) return <LoadingState />;
  if (report.isError) return <ErrorState error={report.error} onRetry={() => void report.refetch()} />;

  const entries = allEntries(report.data);
  // The route's own rule: a holder of daily.publish corrects anyone's entry, everyone else their own, today.
  const mayChange = (entry: CareEntryResponse) => mayPublish || (entry.recordedByMembershipId === me && isToday);
  const draft = report.data.status === 'Draft';

  return (
    <Card
      title={isToday ? t('careDay.title') : t('careDay.titleOn', { date: formatDate(day) })}
      action={reviewRequired && entries.length > 0 && (
        <Badge tone={draft ? 'warning' : 'success'}>{draft ? t('careDay.draft') : t('careDay.published')}</Badge>
      )}
    >
      <Stack>
        {entries.length === 0 && <p className="dg-muted">{t('careDay.empty')}</p>}

        {[publish.error, wake.error, remove.error].map((error, index) => (
          error ? <ErrorState key={index} error={error} /> : null
        ))}

        {entries.map((entry) => {
          const key = `${entry.kind}-${entry.id}`;
          const asleep = entry.kind === 'Sleep' && !entry.endAt;

          if (editing === key) {
            return (
              <EntryEditor
                key={key}
                entry={entry}
                date={day}
                onDone={() => setEditing(null)}
              />
            );
          }

          return (
            <div key={key} className="dg-timeline-entry">
              <span className="dg-timeline-entry__time">{formatTime(entry.kind === 'Sleep' ? entry.startAt : entry.occurredAt)}</span>
              <Stack tight>
                <span>
                  <span aria-hidden="true">{ICON[entry.kind]} </span>
                  <Summary entry={entry} />
                </span>
                {entry.note && <span className="dg-muted">{entry.note}</span>}
                {entry.recordedByName && (
                  <span className="dg-muted">{t('day.recordedBy', { name: entry.recordedByName })}</span>
                )}
                {mayChange(entry) && (
                  <Row>
                    {asleep && (
                      <Button size="default" busy={wake.isPending} onClick={() => wake.mutate(entry)}>
                        {t('careDay.wokeUp')}
                      </Button>
                    )}
                    <Button variant="ghost" onClick={() => setEditing(key)}>{t('action.edit')}</Button>
                    <DeleteButton busy={remove.isPending} onConfirm={() => remove.mutate(entry)} />
                  </Row>
                )}
              </Stack>
            </div>
          );
        })}

        {reviewRequired && draft && entries.length > 0 && (
          mayPublish ? (
            <Row>
              <Button busy={publish.isPending} busyLabel={t('action.saving')} onClick={() => publish.mutate()}>
                {t('day.publish')}
              </Button>
            </Row>
          ) : (
            <Notice tone="info">{t('careDay.waitsForReview')}</Notice>
          )
        )}
        {publish.isSuccess && <Notice tone="success">{t('day.published')}</Notice>}
      </Stack>
    </Card>
  );
}

/** Every entry of the day, the latest first: the one just recorded is the one most likely to need correcting. */
function allEntries(report: DailyReportResponse): CareEntryResponse[] {
  return [
    ...report.meals, ...report.drinks, ...report.activities, ...report.sleeps,
    ...report.moods, ...report.diapers, ...report.notes,
  ].sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : -1));
}

/** One entry in a line, in the words the guardian's day uses. */
function Summary({ entry }: { entry: CareEntryResponse }) {
  const { t, tg, formatTime } = useI18n();

  switch (entry.kind) {
    case 'Meal':
      return (
        <>
          <strong>{entry.mealTypeName ?? t('care.kind.Meal')}</strong>
          {entry.amountEaten && <> · {t(`care.amount.${entry.amountEaten}`)}</>}
          {entry.foodItems && entry.foodItems.length > 0 && <> · {entry.foodItems.map((food) => food.name).join('، ')}</>}
        </>
      );
    case 'Sleep':
      return entry.endAt
        ? <>{t('day.napRange', { from: formatTime(entry.startAt), to: formatTime(entry.endAt) })}</>
        : <strong>{tg('day.sleeping', null, { time: formatTime(entry.startAt) })}</strong>;
    case 'Note':
      return <>{entry.body}</>;
    case 'Diaper':
      return (
        <>
          {entry.catalogItemName ?? t('care.kind.Diaper')}
          {entry.creamApplied && <> · {t('day.creamApplied')}</>}
        </>
      );
    default:
      return <>{entry.catalogItemName ?? t(`care.kind.${entry.kind}`)}</>;
  }
}

/** Two taps to delete, so a thumb that slipped deletes nothing. */
function DeleteButton({ busy, onConfirm }: { busy: boolean; onConfirm: () => void }) {
  const { t } = useI18n();
  const [asking, setAsking] = useState(false);

  return asking ? (
    <>
      <Button variant="danger" busy={busy} onClick={onConfirm}>{t('careDay.confirmDelete')}</Button>
      <Button variant="ghost" onClick={() => setAsking(false)}>{t('action.cancel')}</Button>
    </>
  ) : (
    <Button variant="ghost" onClick={() => setAsking(true)}>{t('careDay.delete')}</Button>
  );
}

/**
 * Corrects one entry: its time, and whatever its kind records.
 *
 * Times are the nursery's (`atTimeOfDay`), so a correction means the same hour on any device. A time later than now
 * on today's day is refused here, as a record of something that has not happened yet.
 */
function EntryEditor({ entry, date, onDone }: { entry: CareEntryResponse; date: string; onDone: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t, settings } = useI18n();

  const catalog = useQuery({ queryKey: ['catalog'], queryFn: () => api.catalog.list() });
  // The active choices, and the one the entry holds even where it has been retired since, so the list shows what
  // will be saved.
  const items = (type: CatalogType, held: number | null | undefined): CatalogItemResponse[] =>
    (catalog.data ?? []).filter((item) => item.catalogType === type && (item.isActive || item.id === held));

  const [time, setTime] = useState(timeOfDay(entry.kind === 'Sleep' ? entry.startAt : entry.occurredAt, settings));
  const [endTime, setEndTime] = useState(entry.endAt ? timeOfDay(entry.endAt, settings) : '');
  const [note, setNote] = useState(entry.note ?? '');
  const [body, setBody] = useState(entry.body ?? '');
  const [amount, setAmount] = useState<MealAmountEaten>(entry.amountEaten ?? 'Most');
  const [mealTypeId, setMealTypeId] = useState<number | ''>(entry.mealTypeId ?? '');
  const [food, setFood] = useState((entry.foodItems ?? []).map((item) => item.name).join('، '));
  const [catalogItemId, setCatalogItemId] = useState<number | ''>(entry.catalogItemId ?? '');
  const [cream, setCream] = useState(entry.creamApplied ?? false);

  const at = (value: string) => atTimeOfDay(date, value, settings);
  const future = (value: string) => value !== '' && new Date(at(value)).getTime() > Date.now();
  const invalid = time === '' || future(time) || future(endTime)
    || (endTime !== '' && endTime < time) || (entry.kind === 'Note' && !body.trim());

  const save = useMutation({
    mutationFn: () => {
      const changes: Record<string, unknown> = { occurredAt: at(time), note: note.trim() };
      switch (entry.kind) {
        case 'Meal':
          Object.assign(changes, {
            amountEaten: amount,
            mealTypeCatalogItemId: mealTypeId === '' ? null : mealTypeId,
            foodItems: food.split(/[,،]/).map((name) => name.trim()).filter(Boolean)
              .map((name) => ({
                catalogItemId: (entry.foodItems ?? []).find((item) => item.name === name)?.catalogItemId ?? null, name,
              })),
          });
          break;
        case 'Sleep':
          Object.assign(changes, { startAt: at(time), endAt: endTime ? at(endTime) : null });
          break;
        case 'Note':
          Object.assign(changes, { body: body.trim() });
          break;
        case 'Diaper':
          Object.assign(changes, { catalogItemId: catalogItemId === '' ? null : catalogItemId, creamApplied: cream });
          break;
        default:
          Object.assign(changes, { catalogItemId: catalogItemId === '' ? null : catalogItemId });
      }
      return api.daily.update(entry.kind, entry.id, changes);
    },
    onSuccess: async () => {
      await invalidateCareDay(queryClient);
      onDone();
    },
  });

  const choices = CHOICE[entry.kind];

  return (
    <Card title={t('careDay.editTitle', { kind: t(`care.kind.${entry.kind}`) })}>
      <Stack>
        <label className="dg-field">
          <span className="dg-field__label">{entry.kind === 'Sleep' ? t('careDay.sleptAt') : t('care.time')}</span>
          <Input type="time" value={time} onChange={(event) => setTime(event.target.value)} />
        </label>

        {entry.kind === 'Sleep' && (
          <label className="dg-field">
            <span className="dg-field__label">{t('careDay.wokeAt')}</span>
            <Input type="time" value={endTime} onChange={(event) => setEndTime(event.target.value)} />
          </label>
        )}

        {entry.kind === 'Meal' && (
          <>
            <Select
              aria-label={t('care.mealType')}
              value={mealTypeId}
              onChange={(event) => setMealTypeId(event.target.value === '' ? '' : Number(event.target.value))}
            >
              <option value="">—</option>
              {items('MealType', entry.mealTypeId).map((item) => <option key={item.id} value={item.id}>{item.nameAr}</option>)}
            </Select>
            <Input
              aria-label={t('care.foodItems')}
              placeholder={t('care.foodItems')}
              value={food}
              onChange={(event) => setFood(event.target.value)}
            />
            <Select
              aria-label={t('care.amountEaten')}
              value={amount}
              onChange={(event) => setAmount(event.target.value as MealAmountEaten)}
            >
              {AMOUNTS.map((value) => <option key={value} value={value}>{t(`care.amount.${value}`)}</option>)}
            </Select>
          </>
        )}

        {choices && (
          <Select
            aria-label={t(`care.kind.${entry.kind}`)}
            value={catalogItemId}
            onChange={(event) => setCatalogItemId(event.target.value === '' ? '' : Number(event.target.value))}
          >
            <option value="">—</option>
            {items(choices, entry.catalogItemId).map((item) => (
              <option key={item.id} value={item.id}>{item.icon ? `${item.icon} ` : ''}{item.nameAr}</option>
            ))}
          </Select>
        )}

        {entry.kind === 'Diaper' && (
          <label className="dg-checkbox">
            <input type="checkbox" checked={cream} onChange={(event) => setCream(event.target.checked)} />
            <span>{t('day.creamApplied')}</span>
          </label>
        )}

        {entry.kind === 'Note' ? (
          <Textarea rows={4} aria-label={t('care.kind.Note')} value={body} onChange={(event) => setBody(event.target.value)} />
        ) : (
          <Textarea rows={2} aria-label={t('care.note')} placeholder={t('care.note')} value={note}
            onChange={(event) => setNote(event.target.value)} />
        )}

        {(future(time) || future(endTime)) && <Notice tone="warning">{t('care.time.future')}</Notice>}
        {save.isError && <ErrorState error={save.error} />}

        <Row>
          <Button busy={save.isPending} busyLabel={t('action.saving')} disabled={invalid} onClick={() => save.mutate()}>
            {t('action.save')}
          </Button>
          <Button variant="ghost" onClick={onDone}>{t('action.cancel')}</Button>
        </Row>
      </Stack>
    </Card>
  );
}
